import { TriangleAlert } from 'lucide-react';
import { useMemo, useState, type ReactElement } from 'react';

import { effectiveProcessType, processTypeLabel, rootsWithoutType } from '../coding';
import { ENTITY_COPY } from '../copy';
import { areaTree } from '../presentation';
import type { ProcessArea } from '../types';
import { filterByStatus, type UseCatalogView } from '../use-catalog-view';
import { useCatalogRows } from '../use-catalogs-admin';
import { useToggleActive } from '../use-toggle-active';
import { CatalogBody } from './CatalogBody';
import { CatalogToolbar } from './CatalogToolbar';
import { ProcessAreaModal } from './ProcessAreaModal';
import { ActiveBadge, RowActions } from './RowActions';

const COPY = ENTITY_COPY.process_area;
const INDENT_PX = 18;

interface Props {
  view: UseCatalogView;
  creating: boolean;
  onCreate: () => void;
  onCloseCreate: () => void;
}

/** Procesos en árbol (macroproceso → subprocesos), con la ruta de fragmentos que
 *  arma el código del documento. El filtro de estado conserva el orden del árbol. */
export function ProcessAreasPanel({
  view,
  creating,
  onCreate,
  onCloseCreate,
}: Props): ReactElement {
  const query = useCatalogRows('process-areas', { enabled: true });
  const toggle = useToggleActive('process-areas', 'process_area', (r) => r.name);
  const [editing, setEditing] = useState<ProcessArea | null>(null);
  const { status } = view.view;
  const all = query.data;
  const tree = useMemo(() => areaTree(all ?? []), [all]);
  const names = useMemo(() => new Map((all ?? []).map((a) => [a.id, a.name])), [all]);
  const visible = new Set(filterByStatus(all ?? [], status).map((a) => a.id));
  const rows = tree.filter((r) => visible.has(r.area.id));
  // G3-2: sin tipo de proceso no se propone el código de los documentos del proceso.
  const untyped = rootsWithoutType(all ?? []);

  return (
    <>
      <CatalogToolbar status={status} onStatus={view.setStatus} />
      {untyped.length > 0 && (
        <div className="callout warn" role="status" aria-label="Procesos sin tipo de proceso">
          <TriangleAlert size={16} aria-hidden="true" />
          <span>
            {untyped.length === 1 ? 'El proceso' : 'Los procesos'}{' '}
            <strong>{untyped.map((a) => a.name).join(', ')}</strong>{' '}
            {untyped.length === 1 ? 'no tiene' : 'no tienen'} tipo de proceso: sin él no se puede
            proponer el código de sus documentos. Edítalo y elige el tipo (01 a 04).
          </span>
        </div>
      )}
      <CatalogBody
        copy={COPY}
        query={query}
        visible={rows.length}
        status={status}
        extraFilter={false}
        onCreate={onCreate}
        onClearFilters={() => view.setStatus('todos')}
      >
        <div className="t-scroll">
          <table className="t t-cards">
            <caption className="sr-only">Procesos de la empresa activa</caption>
            <thead>
              <tr>
                <th scope="col">Proceso</th>
                <th scope="col">Código</th>
                <th scope="col">Tipo de proceso</th>
                <th scope="col">Estado</th>
                <th scope="col" className="t-act">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ area, depth, path }) => {
                const parent = area.parentId === null ? undefined : names.get(area.parentId);
                return (
                  <tr key={area.id} aria-label={area.name}>
                    <td data-label="Proceso">
                      <div style={{ paddingInlineStart: depth * INDENT_PX }}>
                        <strong>{area.name}</strong>
                        {parent !== undefined && (
                          <div className="soft row-note">Dentro de {parent}</div>
                        )}
                      </div>
                    </td>
                    <td data-label="Código" className="code">
                      {path}
                    </td>
                    <td data-label="Tipo de proceso">
                      <ProcessTypeCell area={area} areas={all ?? []} />
                    </td>
                    <td data-label="Estado">
                      <ActiveBadge active={area.isActive} />
                    </td>
                    <td data-label="Acciones" className="t-act">
                      <RowActions
                        label={area.name}
                        active={area.isActive}
                        busy={toggle.isBusy(area.id)}
                        onEdit={() => setEditing(area)}
                        onToggle={() => toggle.request(area)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CatalogBody>
      <ProcessAreaModal
        open={creating || editing !== null}
        editing={editing}
        areas={all ?? []}
        onClose={() => {
          setEditing(null);
          onCloseCreate();
        }}
      />
      {toggle.confirm}
    </>
  );
}

/** Tipo propio del principal, heredado en el subproceso o aviso si falta. */
function ProcessTypeCell({
  area,
  areas,
}: {
  area: ProcessArea;
  areas: readonly ProcessArea[];
}): ReactElement {
  const label = processTypeLabel(effectiveProcessType(area, areas));
  if (label === null) {
    return area.parentId === null ? (
      <span className="badge b-warn">Sin tipo de proceso</span>
    ) : (
      <span className="soft">Sin definir en el principal</span>
    );
  }
  return area.parentId === null ? (
    <span>{label}</span>
  ) : (
    <span className="soft">Hereda {label}</span>
  );
}
